import { Injectable, OnDestroy } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class HolidayThemeService implements OnDestroy {

    private reindeerStartTimeout: number | null = null;
    private reindeerAnimationFrame: number | null = null;
    private reindeerActive = false;
    private christmasObserver: MutationObserver | null = null;

    // ---------- public API ----------

    init(): void {
        if (!this.applyChristmasClass()) {
            this.christmasObserver = new MutationObserver(() => {
                if (this.applyChristmasClass()) {
                    this.christmasObserver?.disconnect();
                    this.christmasObserver = null;
                }
            });

            this.christmasObserver.observe(document.body, { childList: true, subtree: true });

            setTimeout(() => {
                this.christmasObserver?.disconnect();
                this.christmasObserver = null;
            }, 30000);
        }
    }

    destroy(): void {
        this.stopReindeer();

        if (this.christmasObserver) {
            this.christmasObserver.disconnect();
            this.christmasObserver = null;
        }

        const existing = document.querySelector('.reindeer-container') as HTMLElement | null;
        if (existing) existing.remove();

        try {
            delete (window as any).__stopHolidayReindeer;
            delete (window as any).__startHolidayReindeer;
        } catch { }
    }

    ngOnDestroy(): void {
        this.destroy();
    }

    // ---------- helpers ----------

    private getWrapper(): HTMLElement | null {
        return document.querySelector('.app-wrapper') as HTMLElement | null;
    }

    private findNgContentAttr(el: Element | null): string | null {
        if (!el) return null;
        const names = el.getAttributeNames ? el.getAttributeNames() : [];
        const found = names.find(n => n.startsWith('_ngcontent'));
        if (found) return found;

        for (const child of Array.from(el.children)) {
            const res = this.findNgContentAttr(child as Element);
            if (res) return res;
        }
        return null;
    }

    // ---------- snow + background ----------

    private applyChristmasClass(): boolean {
        const wrapper = this.getWrapper();
        if (!wrapper) return false;

        /* wrapper.classList.add('christmas-background');*/

        let container = wrapper.querySelector('.snow-container') as HTMLDivElement | null;
        if (!container) {
            container = document.createElement('div');
            container.className = 'snow-container';
            wrapper.appendChild(container);
        }

        const ngAttr = this.findNgContentAttr(wrapper) || this.findNgContentAttr(document.body);
        if (ngAttr) container.setAttribute(ngAttr, '');

        container.innerHTML = '';

        const flakesCount = 150;
        for (let i = 0; i < flakesCount; i++) {
            const flake = document.createElement('div') as HTMLDivElement;
            flake.className = 'flake';

            if (ngAttr) flake.setAttribute(ngAttr, '');

            const depth = Math.pow(Math.random(), 0.5);
            const left = Math.random() * 100;
            const size = Math.round(2 + (1 - depth) * 5);
            const opacity = (0.5 + (1 - depth) * 0.8).toFixed(2);
            const minDur = 6; const maxDur = 22;
            const duration = (minDur + depth * (maxDur - minDur)).toFixed(2) + 's';
            const delay = (-Math.random() * parseFloat(duration)).toFixed(2) + 's';
            const maxDrift = 120;
            const drift = ((Math.random() * 2 - 1) * maxDrift * (1 - depth)).toFixed(1) + 'px';
            const scale = (0.5 + (1 - depth) * 1.0).toFixed(2);
            const initialX = ((Math.random() * 2 - 1) * 10).toFixed(1) + 'px';

            flake.style.setProperty('--left', `${left}%`);
            flake.style.setProperty('--size', `${size}px`);
            flake.style.setProperty('--opacity', `${opacity}`);
            flake.style.setProperty('--duration', `${duration}`);
            flake.style.setProperty('--delay', `${delay}`);
            flake.style.setProperty('--drift-x', `${drift}`);
            flake.style.setProperty('--scale', `${scale}`);

            flake.style.left = `${left}%`;
            flake.style.transform =
                `translate3d(${initialX}, -${Math.round(10 + Math.random() * 60)}vh, 0) scale(${scale})`;

            container.appendChild(flake);
        }

        //setTimeout(() => wrapper.classList.add('christmas-visible'), 50);

        //const holidayEnabled = localStorage.getItem('holidayEnabled') === 'true';

        (window as any).__stopHolidayReindeer = () => this.stopReindeer();
        (window as any).__startHolidayReindeer = () => this.startReindeer();

        //if (holidayEnabled) {
        //    this.startReindeer();
        //}

        return true;
    }

    // ---------- reindeer ----------

    private scheduleNextReindeer(minGapMs = 60000, maxExtraMs = 240000): void {
        if (this.reindeerStartTimeout) {
            clearTimeout(this.reindeerStartTimeout);
        }
        const gapMs = minGapMs + Math.random() * maxExtraMs;
        this.reindeerStartTimeout = window.setTimeout(() => this.triggerReindeer(), gapMs);
    }

    private stopReindeer(): void {
        if (this.reindeerStartTimeout) {
            clearTimeout(this.reindeerStartTimeout);
            this.reindeerStartTimeout = null;
        }
        if (this.reindeerAnimationFrame) {
            cancelAnimationFrame(this.reindeerAnimationFrame);
            this.reindeerAnimationFrame = null;
        }
        this.reindeerActive = false;
    }

    private startReindeer(): void {
        const wrapper = this.getWrapper();
        if (!wrapper) return;

        let reindeerContainer = wrapper.querySelector('.reindeer-container') as HTMLDivElement | null;
        if (!reindeerContainer) {
            reindeerContainer = document.createElement('div');
            reindeerContainer.className = 'reindeer-container';

            const ngAttr = this.findNgContentAttr(wrapper) || this.findNgContentAttr(document.body);
            if (ngAttr) reindeerContainer.setAttribute(ngAttr, '');

            wrapper.appendChild(reindeerContainer);
        }

        this.triggerReindeer();
        this.scheduleNextReindeer();
    }

    private triggerReindeer(): void {
        const wrapper = this.getWrapper();
        const reindeerContainer = wrapper?.querySelector('.reindeer-container') as HTMLDivElement | null;
        if (!reindeerContainer || this.reindeerActive) return;
        if (reindeerContainer.querySelector('.reindeer')) return;

        this.reindeerActive = true;

        const reindeer = document.createElement('img') as HTMLImageElement;
        reindeer.className = 'reindeer';

        const ngAttr = this.findNgContentAttr(reindeerContainer) || this.findNgContentAttr(document.body);
        if (ngAttr) reindeer.setAttribute(ngAttr, '');

        reindeer.src = '/assets/holiday/christmas-reindeer-silhouette.png';
        reindeer.alt = 'reindeer';

        const topVh = Math.random() * 15;
        reindeer.style.top = `${topVh}vh`;
        reindeer.style.left = '-140vw';

        const durationSec = 16 + Math.random() * 16;
        const widthPx = 200 + Math.random() * 100; // 200�300px
        reindeer.style.setProperty('--reindeer-width', `${Math.round(widthPx)}px`);
        reindeer.style.width = 'var(--reindeer-width)';
        reindeer.style.pointerEvents = 'none';

        reindeerContainer.appendChild(reindeer);

        const keyframes: Keyframe[] = [
            { left: '-140vw', transform: 'rotate(-35deg) translateY(0)', opacity: 0.6, offset: 0 },
            { left: '0vw', transform: 'rotate(0) translateY(0)', opacity: 0.7, offset: 0.5 },
            { left: '140vw', transform: 'rotate(35deg) translateY(0)', opacity: 0.6, offset: 1 }
        ];

        const anim = (reindeer as any).animate?.(keyframes, {
            duration: Math.round(durationSec * 1000),
            easing: 'linear',
            iterations: 1,
            fill: 'forwards'
        });

        const removeReindeer = (el: HTMLElement): void => {
            if (this.reindeerAnimationFrame) {
                cancelAnimationFrame(this.reindeerAnimationFrame);
                this.reindeerAnimationFrame = null;
            }
            if (el.parentElement) el.remove();
            this.reindeerActive = false;
            this.scheduleNextReindeer();
        };

        if (anim && typeof anim.onfinish !== 'undefined') {
            anim.onfinish = () => removeReindeer(reindeer);
        } else {
            const start = performance.now();
            const fallbackMs = Math.round(durationSec * 1000 * 1.5);

            const check = (): void => {
                if (!reindeer || !reindeer.isConnected) return;
                const rect = reindeer.getBoundingClientRect();
                if (rect.left >= window.innerWidth || performance.now() - start > fallbackMs) {
                    removeReindeer(reindeer);
                    return;
                }
                this.reindeerAnimationFrame = requestAnimationFrame(check);
            };

            this.reindeerAnimationFrame = requestAnimationFrame(check);
        }
    }


    // ---------- public control API from header ----------

    /** Called once after feature flag is known. Returns effective enabled state. */
    initFromStorage(isShowChristmasTheme: boolean): boolean {
        const storedValue = localStorage.getItem('holidayEnabled');

        // First time: nothing in storage -> default ON if feature flag allows it
        if (storedValue === null) {
            const enabled = !!isShowChristmasTheme;
            this.enableDisableHolidayTheme(enabled, true); // persist initial choice
            return enabled;
        }

        // Later: respect stored value but also feature flag
        const stored = storedValue === 'true';
        const enabled = stored && isShowChristmasTheme;

        this.enableDisableHolidayTheme(enabled, false); // don't overwrite storage
        return enabled;
    }


    /** Enable / disable holiday theme (called from radio button). */
    enableDisableHolidayTheme(enabled: boolean, persist: boolean = true): void {
        const wrapper = this.getWrapper();
        if (!wrapper) return;

        if (enabled) {
            wrapper.classList.add('christmas-background', 'christmas-visible');
            this.startReindeer();
        } else {
            wrapper.classList.remove('christmas-background', 'christmas-visible');
            this.stopReindeer();
        }

        if (persist) {
            try {
                localStorage.setItem('holidayEnabled', String(enabled));
            } catch { }
        }
    }

}
